import { resendLoginVerifyCodeApi, verifyLoginApi } from "@/api/services/main/auth";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Linkable } from "@/components/elements/Linkable";
import { RESEND_COOLDOWN_SECONDS, SEND_LOGIN_VERIFY_CODE_COOLDOWN_KEY } from "@/constants/common";
import { usePostLoginRedirect } from "@/features/auth/hooks/usePostLoginRedirect";
import { loginVerifySchema } from "@/features/auth/schema/loginVerifySchema";
import { TLoginVerifyForm, TLoginVerifyLocationState } from "@/features/auth/types/loginVerifyForm";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { getInitialCooldownOnReload } from "@/utils/cooldown";
import { setErrorForm, showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { createUrlWithParams } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoginIcon from "@mui/icons-material/Login";
import { Paper, Stack, Typography, styled } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

type TLoginVerifyFormProps = {
  state: TLoginVerifyLocationState;
};

export const LoginVerifyForm: FC<TLoginVerifyFormProps> = ({ state }) => {
  const redirectAfterLogin = usePostLoginRedirect();
  const [resendCooldown, setResendCooldown] = useState(() => getInitialCooldownOnReload(SEND_LOGIN_VERIFY_CODE_COOLDOWN_KEY));

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const methods = useForm<TLoginVerifyForm>({
    mode: "onChange",
    defaultValues: { code: "" },
    resolver: yupResolver(loginVerifySchema),
  });
  const { control } = methods;

  useEffect(() => {
    return () => {
      localStorage.removeItem(SEND_LOGIN_VERIFY_CODE_COOLDOWN_KEY);
    };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      localStorage.removeItem(SEND_LOGIN_VERIFY_CODE_COOLDOWN_KEY);
      return;
    }
    timerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          localStorage.removeItem(SEND_LOGIN_VERIFY_CODE_COOLDOWN_KEY);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resendCooldown > 0]);

  const onSubmit = async (data: TLoginVerifyForm) => {
    try {
      await verifyLoginApi({
        email: state.email,
        externalId: state.externalId,
        code: data.code,
        pendingAuthToken: state.pendingAuthToken,
      });

      redirectAfterLogin({ productCode: state.productCode, redirectUrl: state.redirectUrl });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    const cooldownUntil = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
    localStorage.setItem(SEND_LOGIN_VERIFY_CODE_COOLDOWN_KEY, String(cooldownUntil));
    setResendCooldown(RESEND_COOLDOWN_SECONDS);

    try {
      const payload = {
        email: state.email,
        externalId: state.externalId,
        pendingAuthToken: state.pendingAuthToken,
      };
      await resendLoginVerifyCodeApi(payload);
      toast.success("認証コードを再送しました。メールをご確認ください。");
    } catch (error) {
      showError(error);
      localStorage.removeItem(SEND_LOGIN_VERIFY_CODE_COOLDOWN_KEY);
      setResendCooldown(0);
    }
  };

  const resendLabel = resendCooldown > 0 ? `認証コードを再送する (${resendCooldown}s)` : "認証コードを再送する";

  return (
    <Container>
      <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={loginVerifySchema}>
        <Stack gap="24px" width={400}>
          <Typography
            variant="head20Bold"
            sx={{
              textAlign: "center",
            }}
            color={text.primary}
          >
            二段階認証
          </Typography>
          <Stack flexDirection="column" gap="28px">
            <Typography variant="body14" color={text.secondary}>
              メールへ送付された認証コードを入力してください。
            </Typography>

            <Stack flexDirection="column" gap="24px">
              <FormField label="組織ID">
                <Input size="lg" value={state.externalId} readonly />
              </FormField>

              <FormField label="メールアドレス">
                <Input size="lg" value={state.email} readonly />
              </FormField>

              <FormField control={control} name="code">
                <Input size="lg" placeholder="入力してください" />
              </FormField>
            </Stack>

            <Stack gap="16px">
              <Button type="submit" size="lg" variant="primary" fullWidth>
                <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
                  <LoginIcon fontSize="small" />
                  <Typography variant="body14Semi">ログイン</Typography>
                </Stack>
              </Button>

              <Button type="button" size="sm" variant="ghost" fullWidth onClick={handleResend} disabled={resendCooldown > 0}>
                <Typography variant="body14Semi">{resendLabel}</Typography>
              </Button>
              <Stack alignItems="center">
                <Linkable href={createUrlWithParams(AppRoutes.loginCredentials, state)}>
                  <Stack direction="row" gap={1} alignItems="center">
                    <ArrowBackIcon sx={{ fontSize: 20, color: text.secondary }} />
                    <Typography variant="body14Semi" color={text.secondary}>
                      ログインをやり直す
                    </Typography>
                  </Stack>
                </Linkable>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Form>
    </Container>
  );
};

const Container = styled(Paper)`
  border-radius: 12px;
  padding: 32px;
  box-shadow:
    0 4px 4px -1px rgba(12, 12, 13, 0.05),
    0 4px 4px -1px rgba(12, 12, 13, 0.1) !important;
`;
